import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  PresentationalChat,
  type PresentationalChatRef,
} from '@cotera/client/v0/chat/presentational-chat';
import { ChatWithArtifactPanel } from '@cotera/client/v0/chat/chat-with-artifact-panel';
import {
  CommentsRailOutlet,
  CommentsRailProvider,
} from '@cotera/client/app/components/app/rte';
import { ArtifactsIndexVisibilityProvider } from '@cotera/client/v0/chat/artifacts-index-visibility';
import { ArtifactsIndexToggle } from '@cotera/client/v0/chat/artifacts-index-toggle';
import { ChatTableActionsScope } from '@cotera/client/v0/chat/use-chat-table-actions';
import type { ArtifactRegistration } from '@cotera/client/v0/chat/artifact-registration';
import {
  PromptInputActionAddAttachments,
  PromptInputActionMenu,
  PromptInputActionMenuContent,
  PromptInputActionMenuTrigger,
  type PromptInputMessage,
  PromptInputSubmit,
  PromptInputTools,
} from '@cotera/client/v0/components/ai-elements/prompt-input';
import {
  ModelScopeFactory,
  useAction,
  useActionsContext,
  useInScopeModel,
  useWatchableValue,
  Watchable,
} from '@cotera/client/v0/actions/framework';
import { useModalManager } from '@cotera/client/app/components/app/modal-manager';
import { Actions } from '@cotera/client/v0/actions/framework/context';
import {
  useTrackingOnExit,
  useAnalytics,
  useChatSurface,
  CocoEvent,
  type ChatSurface,
  type ChatEntrypoint,
} from '@cotera/client/app/etc';
import {
  deriveSeedChatSurface,
  deriveChatEntrypoint,
} from '../utils/chat-surface-seed';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';
import { type FileUIPart } from 'ai';
import { ChatArtifactModel } from '@cotera/client/v0/chat/chat-artifact.model';
import { CocoChatModel, useCocoChatModel } from '../models/coco-chat.model';
import {
  MessageQueueModel,
  type QueuedMessage,
} from '../models/message-queue.model';
import { QueuedMessages } from '../components/queued-messages';
import {
  SubmitToolResultsAction,
  ExecuteAllIncompleteToolCallsAction,
  ReplayAction,
  EditAction,
} from '@cotera/client/v0/chat/actions';
import { ExecuteToolAction } from '../actions/coco-execute-tool.action';
import { SubmitToolAction } from '../actions/coco-submit-tool.action';
import { SubmitCocoChatAction } from '../actions/coco-submit-chat.action';
import { StopAction } from '../actions/coco-stop.action';
import { RunToolAction } from '../actions/coco-run-tool.action';
import { useGetBearerToken } from '@cotera/client/app/hooks/use-bearer-token';
import { createWorkspaceDomainLoader } from '../../../hooks/create-workspace-domain-loader';
import { createToolLoader } from '../../../hooks/use-chat-stream';
import {
  useLocation,
  useNavigate,
  useSearchParams,
  useNavigationType,
} from 'react-router-dom';
import type { CitationSource } from '@cotera/client/v0/chat/citations/citation-source';
import { RESOURCE_TYPE_CONFIG } from '../../resources/type/resource-type-config';
import { useFeatureFlagWithLoading } from '../../../hooks/use-feature-flag';
import { getCocoModelOverride } from '../../settings/switch-coco-model.action';
import { useWatchablesFactory } from '../../watchables/watchables-factory-context';
import { cn } from '@cotera/client/v0/lib/utils';
import { Skeleton } from '@cotera/client/v0/components/ui/skeleton';
import { AnimatePresence, motion } from 'motion/react';
import { VoiceRecordingButton } from '@cotera/client/v0/components/voice-recording-button';
import { CocoVoiceTranscriptionCompleteAction } from '../actions/coco-voice-transcription.action';
import { hasContentToSubmit } from '../utils/prompt-utils';
import type { InitialAction } from '../models/pending-chat-submission';
import { Icon, Tooltip } from '../../../components/ui/index';
import { Toggle } from '@cotera/client/v0/components/ui/toggle';
import {
  CocoInputHeader,
  useHeaderContent,
} from '../components/coco-input-header';
import { useAppModel } from '../../shell/app.model';
import {
  consumePendingChatSubmission,
  peekPendingChatSubmission,
  removePendingChatSubmission,
  type PendingChatSubmission,
} from '../models/pending-chat-submission';
import { removeChatDraft } from '../models/chat-draft';
import { useRestoreChatDraft } from '../utils/use-restore-chat-draft';
import type { ArtifactDescriptor } from '@cotera/client/v0/chat/artifacts';
import type { Conversation, MessageClass } from '@cotera/sdk/core';
import { useMarkMessagesAsRead } from '../../../hooks/use-mark-messages-as-read';
import {
  ViewLoadedToolsAction,
  filterDisplayableLoadedTools,
} from '../actions/view-loaded-tools.action';
import { getV1ToolRenderer } from '../../components/v1-tool-renderers';
import { PROVIDER_ICONS } from '@cotera/client/v0/components/tool-connections';
import { getProviderFromToolKey } from '@cotera/client/v0/components/tool-connections/utils';
import { ProviderDetailPage } from '@cotera/client/v0/components/tool-connections/provider-detail';
import { useAvailableTools } from '../../../hooks/use-available-tools';
import { agentKeyFromToolKey } from '../../../hooks/use-agents-display-names';
import { useAgentsIdByKey } from '../../../hooks/use-agents-display-names';
import { TypingTitle } from '../../agent/typing-title';
import { useNavigateOnResourceMentionClick } from '../../../hooks/use-resource-mention-navigation';
import {
  coco,
  ResourceType,
  ORGANIZE_ARTIFACT_TYPE,
  organizeModeOf,
} from '@cotera/api';
import { FavoriteToggleButton } from '../../resources/favorites';
import { AddResourceArtifactAction } from '../actions/coco-add-resource-artifact.action';
import { RemoveResourceArtifactAction } from '../actions/coco-remove-resource-artifact.action';
import {
  isArtifactViewable,
  parseResourceMention,
  parseWorkflowToolMention,
} from '@cotera/client/v0/chat/artifacts';
import { ResourceSelectionModel } from '../../resources/selection/resource-selection.model';
import type { MentionClickData } from '@cotera/client/v0/components/ai-elements/readonly-editor-state';
import { MoreHorizontal, MoreVertical } from 'lucide-react';
import { ArchivedNotice } from '../../resources/archived-notice';
import {
  registerApprovalHijacker,
  HijackerQueueModel,
} from '@cotera/client/v0/chat/input-hijackers';
import { ToolApprovalHijacker } from '../../components/tool-approval-hijacker';
import { registerMessageFooter } from '@cotera/client/v0/chat/message-footers';
import { WaitingForApprovalPlaceholder } from '@cotera/client/v0/chat/message-footers/waiting-for-approval-placeholder';
import {
  hasToolNeedingApproval,
  getPendingApprovalCount,
} from '@cotera/client/v0/chat/message-footers/tool-approval-footer';
import {
  CocoThinkingPlaceholder,
  COCO_THINKING_MESSAGE_FOOTER_ID,
} from '../components/coco-thinking-placeholder';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@cotera/client/v0/components/ui/dropdown-menu';
import { Button } from '@cotera/client/v0/components/ui/button';
import { ShareResourceLinkAction } from '../../resources/actions/share-resource-link.action';
import { MoveToFolderAction } from '../../resources/actions/move-to-folder.action';
import { ArchiveResourceAction } from '../../resources/actions/archive-resource.action';
import { RenameChatAction } from '../../resources/actions/rename-chat.action';
import { CocoToggleAutoApproveAction } from '../actions/coco-toggle-auto-approve.action';
import { CocoTogglePlanModeAction } from '../actions/coco-toggle-plan-mode.action';
import { CocoCompactAction } from '../actions/coco-compact.action';
import { SaveChatArtifactsAction } from '../actions/save-chat-artifacts.action';
import {
  ResourcePageShell,
  useResourceBreadcrumbs,
} from '../../resources/resource-page-shell';
import { useChatBreadcrumbFolder } from '../utils/use-chat-breadcrumb-folder';
import { shareableResourceForArtifact } from '@cotera/client/v0/chat/artifacts/resource-artifact';
import {
  artifactFocusViewHref,
  chatHrefForArtifactFocusView,
} from '../artifacts/artifact-focus-view-route';
import { getStackFrameStatusInfo } from '../utils/coco-stack-frame-status';
import { ChatCostBadge } from '../components/chat-cost-badge';
import { ChatShareIndicator } from '../components/chat-share-indicator';
import { useIsBelowSm } from '@cotera/client/app/hooks/use-breakpoint';
import {
  useChatHeaderMeta,
  type ChatHeaderMeta,
} from '../components/use-chat-header-meta';
import {
  getChatSeedStrategy,
  hasChatSeedInSearchParams,
  parseChatSeedFolderId,
  parseChatSeedFromSearchParams,
  resolveChatSeedContext,
  stripChatSeedSearchParams,
} from '../../chat-seed/index';

const { isAgentToolKey } = coco;

const MAX_STACKED_ICONS = 3;

const LoadedToolIcons: React.FC<{ toolKeys: string[] }> = ({ toolKeys }) => {
  const uniqueProviders = useMemo(() => {
    const seen = new Set<string>();
    const result: string[] = [];
    for (const key of toolKeys) {
      const provider = getProviderFromToolKey(key);
      if (!seen.has(provider)) {
        seen.add(provider);
        result.push(provider);
      }
    }
    return result;
  }, [toolKeys]);

  const visibleProviders = uniqueProviders.slice(0, MAX_STACKED_ICONS);
  const overflow = uniqueProviders.length - MAX_STACKED_ICONS;

  return (
    <div className="flex items-center">
      <div className="flex items-center -space-x-1.5">
        {visibleProviders.map((provider, i) => {
          const Icon =
            PROVIDER_ICONS[provider as keyof typeof PROVIDER_ICONS] ??
            (() => null);
          return (
            <div
              key={provider}
              className={cn(
                'flex items-center justify-center rounded-full bg-muted border-2 border-background',
                'w-6 h-6'
              )}
              style={{ zIndex: visibleProviders.length - i }}
            >
              <div className="scale-[0.6]">
                <Icon />
              </div>
            </div>
          );
        })}
      </div>
      {overflow > 0 && (
        <span className="text-[10px] font-medium text-muted-foreground ml-0.5">
          +{overflow}
        </span>
      )}
    </div>
  );
};

function usePendingSubmission(
  conversationId: string,
  submitAction: {
    execute: (params: {
      text?: string;
      editorState?: { type: 'doc'; content?: unknown[] };
      files?: FileUIPart[];
      systemSuffix?: string;
      messageType?: 'chat' | 'hidden';
      initialArtifacts?: Array<{
        artifactType: string;
        artifactId?: string;
        data: unknown;
      }>;
      initialToolKeys?: string[];
      initialActions?: InitialAction[];
      agentId?: string;
    }) => Promise<unknown>;
  },
  pending: PendingChatSubmission | null,
  cocoModel: ReturnType<typeof useCocoChatModel>
): ArtifactDescriptor[] {
  const [initialArtifacts, setInitialArtifacts] = React.useState<
    ArtifactDescriptor[]
  >([]);
  const sentPendingSubmissionRef = useRef(false);

  useEffect(() => {
    if (sentPendingSubmissionRef.current) {
      return;
    }

    const toSubmit = pending ?? consumePendingChatSubmission(conversationId);
    if (toSubmit === null) {
      return;
    }

    sentPendingSubmissionRef.current = true;

    if (pending !== null) {
      removePendingChatSubmission(conversationId);
    }

    if (toSubmit.initialArtifacts !== undefined) {
      setInitialArtifacts(
        toSubmit.initialArtifacts.map((a) => ({
          type: a.artifactType,
          artifactId: a.artifactId ?? '',
          payload: a.data ?? null,
          messageId: '',
          toolCallId: '',
          toolName: '',
        }))
      );
    }

    if (toSubmit.seedOnly === true) {
      cocoModel.setDeferredSeedContext({
        initialArtifacts: toSubmit.initialArtifacts,
        initialToolKeys: toSubmit.initialToolKeys,
        initialActions: toSubmit.initialActions,
        ...(toSubmit.agentId !== undefined && { agentId: toSubmit.agentId }),
        ...(toSubmit.displayTitle !== undefined && {
          displayTitle: toSubmit.displayTitle,
        }),
      });
      return;
    }

    if (hasContentToSubmit(toSubmit)) {
      void submitAction.execute({
        text: toSubmit.text,
        editorState: toSubmit.editorState,
        files: toSubmit.files as FileUIPart[] | undefined,
        systemSuffix: toSubmit.systemSuffix,
        messageType: toSubmit.messageType,
        initialArtifacts: toSubmit.initialArtifacts?.map((a) => ({
          artifactType: a.artifactType,
          artifactId: a.artifactId,
          data: a.data ?? null,
        })),
        initialToolKeys: toSubmit.initialToolKeys,
        initialActions: toSubmit.initialActions,
        ...(toSubmit.agentId !== undefined && { agentId: toSubmit.agentId }),
      });
    }
  }, [conversationId, submitAction, pending, cocoModel]);

  // The organize-library artifact is persisted onto the conversation when the
  // Organize button opens it (see `openOrganizeLibrary` server-side), so it
  // rehydrates from the conversation snapshot on reload like any other artifact —
  // no client-side re-fetch needed.

  return initialArtifacts;
}

/** Line widths for agent message lines (0-1) */
const AGENT_LINE_WIDTHS = [
  0.88, 0.92, 0.72, 0.85, 0.68, 0.78, 0.82, 0.65,
] as const;

const SKELETON_CLASS = 'bg-coco-accent';

const AgentMessageSkeleton: React.FC<{ lineWidths: readonly number[] }> = ({
  lineWidths,
}) => (
  <div className="space-y-1.5 max-w-[80%]">
    {lineWidths.map((width, i) => (
      <Skeleton
        key={i}
        className={cn(SKELETON_CLASS, 'h-2 rounded-full')}
        style={{ width: `${width * 100}%` }}
      />
    ))}
  </div>
);

const CocoChatSkeletonContent: React.FC = () => (
  <div className="relative flex-1 overflow-y-auto">
    <div
      className="p-4 max-w-5xl mx-auto space-y-6 [mask-image:linear-gradient(to_bottom,black_0%,black_45%,transparent_100%)] [-webkit-mask-image:linear-gradient(to_bottom,black_0%,black_45%,transparent_100%)]"
      style={{ minHeight: '100%' }}
    >
      {/* User message: single larger bubble (right-aligned) */}
      <div className="flex justify-end py-2">
        <Skeleton
          className={cn(SKELETON_CLASS, 'h-12 w-72 max-w-[80%] rounded-lg')}
        />
      </div>

      {/* Agent messages: thinner, tighter lines (left-aligned) */}
      <div className="flex flex-col gap-4">
        <AgentMessageSkeleton lineWidths={AGENT_LINE_WIDTHS} />
        <AgentMessageSkeleton lineWidths={AGENT_LINE_WIDTHS} />
        <AgentMessageSkeleton lineWidths={AGENT_LINE_WIDTHS} />
      </div>
    </div>
  </div>
);

/**
 * The chat's loading screen, bar included.
 *
 * The bare skeleton below it is the *body* of a chat; it draws no page bar, and
 * for the two states that render it in place of the whole page — feature flags
 * still resolving, and the Suspense on `useAvailableTools` — that left the
 * window with no breadcrumb and, on mobile, no nav trigger. The app shell now
 * fills a missing trigger in (see `SidebarTriggerPresenceProvider`), but a bar
 * that appears when the chat finishes loading and shifts the conversation down
 * with it is its own problem. Drawing the real bar up front means the skeleton
 * and the chat that replaces it are the same shape.
 *
 * `breadcrumbs={undefined}` is the shell's own "still resolving" input: it
 * holds a two-chip skeleton in the trail rather than flashing a placeholder
 * name.
 */
const CocoChatSkeletonPage: React.FC = () => (
  <ResourcePageShell breadcrumbs={undefined}>
    <CocoChatSkeletonContent />
  </ResourcePageShell>
);

const CocoChatVoiceSubmitButton: React.FC<{
  chatRef: React.RefObject<PresentationalChatRef>;
}> = ({ chatRef }) => {
  const voiceTranscriptionAction = useAction(
    CocoVoiceTranscriptionCompleteAction
  );
  return (
    <VoiceRecordingButton
      onTranscriptionComplete={async (text) =>
        await voiceTranscriptionAction.execute({
          transcribedText: text,
          chatRef,
        })
      }
    />
  );
};

// Subscribes to status + hasInput itself so a keystroke only re-renders this
// button subtree — not the whole CocoChatContent tree. The three branches
// mirror the original inline JSX in the ChatInput's customSubmitButton slot.
const CocoChatSubmitButton: React.FC<{
  chatRef: React.RefObject<PresentationalChatRef>;
}> = ({ chatRef }) => {
  const model = useCocoChatModel();
  const stopAction = useAction(StopAction);
  const status = useWatchableValue<
    'ready' | 'streaming' | 'submitted' | 'error' | 'loading'
  >(model.statusWatchable);
  const hasInput = useWatchableValue(model.hasInputWatchable);

  if (status === 'streaming' || status === 'submitted') {
    // Same component (and size) as the send button so the pair matches. It is a
    // form submit by default, but submitting while a run is in flight now queues
    // the composer text, so Stop is forced to `type="button"` + onClick to stop
    // instead. `status` drives the square/spinner icon.
    const stopButton = (
      <PromptInputSubmit
        type="button"
        aria-label="Stop generating"
        size="icon-sm"
        status={status}
        className={cn(
          '!bg-gradient-to-r !from-primary !via-primary/80 !to-primary !bg-[length:200%_100%] !animate-shimmer'
        )}
        onClick={() => {
          void stopAction.execute({ systemSuffix: model.systemSuffix });
        }}
      />
    );

    // With text in the composer, offer to queue it (Enter queues too) while
    // keeping Stop reachable beside it. Queue is the secondary, deferred action,
    // so it is an outline button next to the primary shimmer Stop.
    if (hasInput) {
      return (
        <>
          {stopButton}
          <PromptInputSubmit
            aria-label="Queue message"
            size="icon-sm"
            variant="outline"
          />
        </>
      );
    }

    return stopButton;
  }

  if (!hasInput) {
    return <CocoChatVoiceSubmitButton chatRef={chatRef} />;
  }

  return (
    <PromptInputSubmit
      aria-label="Send message"
      size="icon-sm"
      status={status}
    />
  );
};

// Stable fallback so useWatchableValue has a watchable even before the queue
// model resolves (it is registered per conversation, so this is defensive only).
const EMPTY_QUEUE_WATCHABLE = Watchable.fromValue<QueuedMessage[]>([]);

const CocoChatInputFooter: React.FC<{
  chatRef: React.RefObject<PresentationalChatRef>;
}> = ({ chatRef }) => {
  const model = useCocoChatModel();
  const hasInput = useWatchableValue(model.hasInputWatchable);
  const autoApproveTools = useWatchableValue(model.autoApproveToolsWatchable);
  const planMode = useWatchableValue(model.planModeWatchable);
  const loadedToolKeys = useWatchableValue<string[]>(
    model.loadedToolsWatchable
  );
  const toggleAutoApproveAction = useAction(CocoToggleAutoApproveAction);
  const togglePlanModeAction = useAction(CocoTogglePlanModeAction);
  const displayableToolKeys = useMemo(
    () => filterDisplayableLoadedTools(loadedToolKeys),
    [loadedToolKeys]
  );
  const viewLoadedToolsAction = useAction(ViewLoadedToolsAction);
  const voiceTranscriptionAction = useAction(
    CocoVoiceTranscriptionCompleteAction
  );
  // Closing the artifact panel leaves its tabs open but off screen; this is the
  // way back to them from the composer.

  return (
    <div className="flex justify-between items-center w-full px-1 min-h-8">
      <PromptInputTools>
        <PromptInputActionMenu>
          <PromptInputActionMenuTrigger />
          <PromptInputActionMenuContent>
            <PromptInputActionAddAttachments />
          </PromptInputActionMenuContent>
        </PromptInputActionMenu>
        {hasInput && (
          <VoiceRecordingButton
            onTranscriptionComplete={async (text) =>
              await voiceTranscriptionAction.execute({
                transcribedText: text,
                chatRef,
              })
            }
          />
        )}
        {displayableToolKeys.length > 0 && (
          <Tooltip tooltipContent="View loaded tools" side="top">
            <button
              type="button"
              onClick={() =>
                void viewLoadedToolsAction.execute({ loadedToolKeys })
              }
              aria-label="View loaded tools"
              className="flex items-center cursor-pointer hover:opacity-80 transition-opacity"
            >
              <LoadedToolIcons toolKeys={displayableToolKeys} />
            </button>
          </Tooltip>
        )}
        <Tooltip tooltipContent="Auto-approve all tools" side="top">
          <Toggle
            pressed={autoApproveTools}
            onPressedChange={(pressed) =>
              void toggleAutoApproveAction.execute({ pressed })
            }
            size="sm"
            variant="ghost"
            aria-label="Toggle auto-approve tools"
            className={cn(
              'cursor-pointer',
              autoApproveTools && 'bg-primary/10! text-primary! rounded-sm!'
            )}
          >
            <Icon icon="zap" />
          </Toggle>
        </Tooltip>
        <Tooltip tooltipContent="Plan mode" side="top">
          <Toggle
            pressed={planMode}
            onPressedChange={(pressed) =>
              void togglePlanModeAction.execute({ pressed })
            }
            size="sm"
            variant="ghost"
            aria-label="Toggle plan mode"
            className={cn(
              'cursor-pointer',
              planMode && 'bg-primary/10! text-primary! rounded-sm!'
            )}
          >
            <Icon icon="light-bulb" />
          </Toggle>
        </Tooltip>
      </PromptInputTools>
    </div>
  );
};

/**
 * Opens the chat's share dialog. Shared by the header's Share button and the
 * indicator beside it, and by the copy of that indicator mirrored into the
 * artifact panel's bar — all three are the same dialog, so they are the same
 * callback.
 */
const useOpenChatShare = (): (() => void) => {
  const shareAction = useAction(ShareResourceLinkAction);
  return useCallback(() => {
    void shareAction.execute({ fromChat: true });
  }, [shareAction]);
};

const CocoChatActions: React.FC<{ meta: ChatHeaderMeta }> = ({ meta }) => {
  const moveAction = useAction(MoveToFolderAction);
  const archiveAction = useAction(ArchiveResourceAction);
  const openShare = useOpenChatShare();
  // The bar is one row, the actions cluster is `shrink-0`, and the trail beside
  // it is the only thing that gives — so on a phone this cluster at its full
  // width left the chat's own name squeezed to a couple of characters. Below
  // `sm` the two readouts stand down and the share state travels in its compact
  // form, which is the same trade the artifact panel's bar already makes.
  const isNarrow = useIsBelowSm();
  return (
    <div className="flex items-center gap-2 sm:gap-4">
      {/* What is true about this chat — what it has cost, who can see it — kept
          apart from what you can do to it. At one flat `gap-4` the four sat as
          peers, which they are not: two are readouts and two are controls.
          Both readouts go silent on their own, so the group folds away rather
          than leaving a gap where it used to be. */}
      <div className="flex items-center gap-2 [&:empty]:hidden">
        {/* What a conversation has cost is a desktop readout: useful to glance
            at, never worth the width it takes out of the title on a phone. The
            same number is in the chat's developer details either way. */}
        {isNarrow ? null : <ChatCostBadge />}
        <ChatShareIndicator
          meta={meta}
          onOpenShare={openShare}
          compact={isNarrow}
        />
      </div>
      {/* `gap-3` outside the icon pair, `gap-1` within it. One value cannot
          space these evenly, because the three controls do not present the same
          edges: Share and a pressed toggle are filled blocks whose edge *is*
          their background, while the `⋯` is a 13px glyph centred in a 26px box
          and so carries 6.5px of its own transparent padding. At a flat `gap-1`
          the apparent gaps were 3.25px on Share's side against 9.75px on the
          menu's, which is the toggle looking glued to Share. `gap-3` cancels
          that padding, landing both at 9.75px with the toggle lit and 16.25px
          with it dark.

          Sizes here are rem against the app's `html { font-size: 0.8125rem }`,
          so they are ~19% of the numbers Tailwind's names suggest. */}
      <div className="flex items-center gap-3">
        {/* Share is low-frequency but high-expectation — the one header action
          people actively hunt for, because every comparable editor puts it
          top-right. That is why it stays out of the `⋯` menu: burying it would
          cost the *first* share in particular, since the avatars beside it only
          appear once a chat is already shared. */}
        {/* Filled and labelled, deliberately louder than the two beside it:
            it is the one action people hunt for by name. What was inconsistent
            was never its colour but the sizes around it, with `sm` at 26px tall
            and the icon buttons at 19.5px boxes beside it. */}
        <Button
          variant="default"
          size="sm"
          aria-label="Share chat"
          onClick={openShare}
        >
          <Icon icon="share" />
          {/* The glyph alone below `sm`. `aria-label` above already carries the
              word, so nothing is lost to a screen reader — and the icon is the
              one every comparable editor puts in this corner. */}
          <span className="hidden sm:inline">Share</span>
        </Button>
        {/* Beside the `⋯` rather than up with the cost and share readouts: it is
            a control, and it is the only other icon-only button in the bar, so
            the two sit as a pair at the end with Share's labelled button ahead
            of them. It comes and goes with the artifacts, which is why it is
            on this side of the pair — a control that appears and disappears
            must not shift the position of one that is always there. */}
        <div className="flex items-center gap-1">
          <ArtifactsIndexToggle />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              {/* `icon-md` (26px), matching Share's `sm` height and the index
                  toggle beside it. At `icon-sm` this was a 19.5px box between
                  two 26px ones, which is why the bar read as three unrelated
                  controls rather than one row. */}
              <Button variant="ghost" size="icon-md" aria-label="Chat actions">
                {/* `size-4`, like every other glyph in this bar — see the note
                    on the index toggle for why these stay on the rem scale
                    rather than an absolute pixel size.

                    Kebab on a phone, meatball on a desktop. The bar is a row
                    and horizontal room is what it is short of there: a
                    horizontal `⋯` spends its width on the axis the bar is
                    already fighting over, and sits beside a Share button that
                    has just dropped its own label for the same reason. Turned
                    upright the mark reads as the same control, drawn along the
                    axis with room to spare — and it is what a phone user meets
                    in every other app's overflow menu. Desktop keeps the
                    meatball, which is what the rest of this app's row menus
                    use.

                    One glyph swapped by CSS rather than by a media-query hook:
                    the two marks are the same control in the same place, so
                    nothing about the button — its label, its menu, its state —
                    has to know which is showing. */}
                <MoreVertical className="size-4 sm:hidden" />
                <MoreHorizontal className="size-4 max-sm:hidden" />
              </Button>
            </DropdownMenuTrigger>
            {/* `sideOffset` above the default 4: `align="end"` hangs the menu back
            under the Share button beside the trigger, so at 4px it read as
            hanging off Share rather than sitting below the `⋯` that opened it. */}
            {/* No Rename here. The title in the bar is the rename — click it —
              and a menu row that opened a dialog to do the same thing was a
              second interaction model for one operation. `RenameChatAction`
              itself is untouched and still registered: list rows and any
              generic action surface reach it, and those are the callers its
              dialog exists for. */}
            <DropdownMenuContent align="end" side="bottom" sideOffset={8}>
              <DropdownMenuItem onSelect={() => void moveAction.execute({})}>
                <Icon icon="folder" />
                Move to folder
              </DropdownMenuItem>
              <DropdownMenuItem
                variant="destructive"
                onSelect={() => void archiveAction.execute({})}
              >
                <Icon icon="archive-box" />
                Archive
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
};

/**
 * The chat's share state, mirrored into the artifact panel's bar.
 *
 * Read-only. The Share itself lives in the bar above the split, where it
 * visibly governs both panes; this is the artifact's copy of *who can see it*,
 * and clicking it opens that same dialog. It is also the flag that suppresses a
 * resource's own Share while it sits here — see `useHostOwnsSharing`.
 *
 * Sharing here is a single grant over the conversation *and* everything it
 * produced — one dialog, one set of grantees, see `collectShareableChatResources`.
 * A bar that ran across the whole window made that obvious; a bar per pane does
 * the opposite, and the Share sitting in the chat's half now reads as sharing
 * only the chat.
 *
 * So what crosses the seam is the state, not the button: the same avatars over
 * the artifact as over the conversation, saying the two are one thing shared
 * once. Clicking either opens the same dialog. It costs an avatar stack that is
 * only there once a chat is actually shared, rather than a rival Share button a
 * few pixels from the artifact's own.
 */
const CocoChatPanelAccess: React.FC<{ meta: ChatHeaderMeta }> = ({ meta }) => {
  const openShare = useOpenChatShare();
  return <ChatShareIndicator meta={meta} onOpenShare={openShare} compact />;
};

// Dynamic headings over the Organize dual-view's empty chat input — the Organize
// counterpart to the per-resource seed headings (see agent/dataset/workflow seed
// chrome). One is picked at random per open and typed out by `TypingTitle`, and
// names the library being organized so the shared one never reads as "yours".
const ORGANIZE_SEED_HEADINGS = [
  (library: string) =>
    `Have Coco organize ${library} for you, or arrange the folders yourself`,
  (library: string) =>
    `Let Coco sort ${library}, or move things around yourself`,
  (library: string) =>
    `Ask Coco to organize ${library}, or adjust the folders yourself`,
  (library: string) =>
    `Use Coco to organize ${library}, or rearrange the folders yourself`,
] as const;

function pickOrganizeHeading(): (typeof ORGANIZE_SEED_HEADINGS)[number] {
  return (
    ORGANIZE_SEED_HEADINGS[
      Math.floor(Math.random() * ORGANIZE_SEED_HEADINGS.length)
    ] ?? ORGANIZE_SEED_HEADINGS[0]
  );
}

const CocoChatContent: React.FC<{
  pending?: PendingChatSubmission | null;
  hasUrlSeed?: boolean;
  /** See {@link CocoChat}. */
  focusViewArtifactId?: string;
}> = ({ pending = null, hasUrlSeed = false, focusViewArtifactId }) => {
  const appModel = useAppModel();
  const cocoModel = useCocoChatModel();
  const chatRef = useRef<PresentationalChatRef>(null);
  const conversationId = cocoModel.conversationId;
  const submitChatAction = useAction(SubmitCocoChatAction);
  const stopAction = useAction(StopAction);
  const runToolAction = useAction(RunToolAction);
  const client = useTenantedClient();
  const hijackerQueueModel = useInScopeModel(HijackerQueueModel);
  const messageQueueModel = useInScopeModel(MessageQueueModel);
  const queuedMessages = useWatchableValue<QueuedMessage[]>(
    messageQueueModel?.queueWatchable ?? EMPTY_QUEUE_WATCHABLE
  );
  // While messages are queued, the live-status drawer ("Locked and loaded", etc.)
  // is redundant with the queue caption, so we hide it to avoid stacking two
  // "in progress" affordances above the composer.
  const hasQueuedMessages = queuedMessages.length > 0;
  const [searchParams, setSearchParams] = useSearchParams();
  const addResourceArtifactAction = useAction(AddResourceArtifactAction);
  const removeResourceArtifactAction = useAction(RemoveResourceArtifactAction);
  const modalManager = useModalManager((s) => s.actions);
  const { providers } = useAvailableTools();
  const agentsIdByKey = useAgentsIdByKey();
  const navigate = useNavigate();
  const orgSlug = useWhoami((s) => s.org.slug);
  const personality = useWatchableValue(appModel.personality);
  const headerContent = useHeaderContent(personality);

  useEffect(() => {
    return cocoModel.registerInputContentSetter((value) => {
      if (value.editorState !== undefined) {
        chatRef.current?.setInputEditorState(value.editorState);
        return;
      }
      chatRef.current?.setInputValue(value.text ?? '');
    });
  }, [cocoModel]);

  const sessionStartedAt = useRef(Date.now());
  // Kept current by the chat-viewed effect below so the unmount handler and the
  // in-scope actions all report a consistent surface.
  const surfaceRef = useRef<ChatSurface>('scratch');
  useTrackingOnExit(() => {
    const finalMessages = cocoModel.messagesWatchable.snapshot();
    let userMessageCount = 0;
    let assistantMessageCount = 0;
    for (const m of finalMessages) {
      if (m.isAssistantMessage()) {
        assistantMessageCount += 1;
      } else if (m.role === 'user') {
        userMessageCount += 1;
      }
    }
    return {
      event: CocoEvent.ChatSessionEnded,
      args: {
        conversationId: cocoModel.conversationId,
        surface: surfaceRef.current,
        durationMs: Date.now() - sessionStartedAt.current,
        userMessageCount,
        assistantMessageCount,
      },
    };
  });

  const providerByKey = useMemo(() => {
    const map = new Map<string, (typeof providers)[number]>();
    for (const p of providers) {
      map.set(p.provider, p);
    }
    return map;
  }, [providers]);

  const navigateResourceMention = useNavigateOnResourceMentionClick();

  const handleMentionClick = useCallback(
    (data: MentionClickData) => {
      // Resource mentions the artifact viewer can render (agent/document/folder)
      // open in the artifact panel instead of navigating away.
      const resource = parseResourceMention(data);
      if (resource !== null && isArtifactViewable(resource.resourceType)) {
        void addResourceArtifactAction.execute({
          resourceType: resource.resourceType,
          resourceId: resource.resourceId,
        });
        return;
      }
      // Workflow @-mentions are tool keys (`coco/workflow-…`) — open the
      // workflow artifact the same way agent tool mentions open agents.
      // Must run before navigateResourceMention, which also matches these keys.
      const workflowId = parseWorkflowToolMention(data);
      if (workflowId !== null) {
        void addResourceArtifactAction.execute({
          resourceType: ResourceType.Workflow,
          resourceId: workflowId,
        });
        return;
      }
      if (navigateResourceMention(data)) {
        return;
      }
      const toolKey = data.toolKey ?? data.name;
      if (toolKey === undefined || toolKey === '') {
        return;
      }
      if (isAgentToolKey(toolKey)) {
        const agentKey = agentKeyFromToolKey(toolKey);
        const agentId =
          agentKey !== null ? agentsIdByKey.get(agentKey) : undefined;
        if (agentId !== undefined) {
          void addResourceArtifactAction.execute({
            resourceType: ResourceType.Agent,
            resourceId: agentId,
          });
        }
      } else {
        const providerKey = getProviderFromToolKey(toolKey);
        const provider = providerByKey.get(providerKey);
        if (provider !== undefined) {
          modalManager.push({
            size: 'lg',
            view: () => (
              <ProviderDetailPage
                provider={provider}
                expandedToolKey={toolKey}
              />
            ),
          });
        }
      }
    },
    [
      navigateResourceMention,
      addResourceArtifactAction,
      modalManager,
      providerByKey,
      agentsIdByKey,
    ]
  );

  // Inline citation chips route here. Web sources keep their default anchor
  // behaviour (a new tab); Cotera resources open in the artifact panel, the
  // same way resource @-mentions do.
  const handleCitationClick = useCallback(
    (source: CitationSource) => {
      if (source.kind !== 'resource') {
        return;
      }
      if (isArtifactViewable(source.resourceType)) {
        void addResourceArtifactAction.execute({
          resourceType: source.resourceType,
          resourceId: source.resourceId,
        });
        return;
      }
      navigate(
        `/${orgSlug}/${
          RESOURCE_TYPE_CONFIG[source.resourceType].itemRouteSegment
        }/${source.resourceId}`
      );
    },
    [addResourceArtifactAction, navigate, orgSlug]
  );

  // Anything the UI pushes into the artifact panel — a dataset opened from a link
  // inside another artifact, a workflow opened from the resource navigator — is
  // recorded on the chat, the same way a clicked resource mention is, so Coco's
  // active context tracks what the user is actually looking at.
  const handleRegisterArtifact = useCallback(
    (registration: ArtifactRegistration) => {
      void addResourceArtifactAction.execute({
        resourceType: registration.resourceType,
        resourceId: registration.resourceId,
      });
    },
    [addResourceArtifactAction]
  );

  // Closing a tab is the inverse: retract the artifact so it stops coming back on
  // reload and drops out of Coco's active context.
  const handleUnregisterArtifact = useCallback(
    (artifactId: string) => {
      void removeResourceArtifactAction.execute({ artifactId });
    },
    [removeResourceArtifactAction]
  );

  const messagesStatus = useWatchableValue<'loading' | 'ready'>(
    cocoModel.messagesStatusWatchable
  );
  const messages = useWatchableValue<MessageClass[]>(
    cocoModel.messagesWatchable
  );
  const conversation = useWatchableValue<Conversation | null>(
    cocoModel.conversationWatchable
  );
  const currentUserId = useWhoami((state) => state.whoami.id);
  const firstUnreadMessageId = conversation?.getFirstUnreadMessageId() ?? null;

  const clearUrlSeedIfPresent = useCallback(() => {
    if (!hasChatSeedInSearchParams(searchParams)) {
      return;
    }
    setSearchParams(stripChatSeedSearchParams(searchParams), { replace: true });
  }, [searchParams, setSearchParams]);

  const handleSubmit = useCallback(
    async (message: PromptInputMessage) => {
      const submitParams = {
        ...(message.type === 'text'
          ? { text: message.text }
          : { editorState: message.editorState }),
        files: message.files,
        systemSuffix: cocoModel.systemSuffix,
      };

      if (hasContentToSubmit(submitParams)) {
        const activeItem =
          hijackerQueueModel?.queueStateWatchable.snapshot().activeItem;
        if (activeItem !== null && activeItem !== undefined) {
          if (activeItem.part.approvalStatus?.() === 'required') {
            const denyResult = await runToolAction.execute({
              toolCallId: activeItem.part.toolCallId,
              conversationId,
              action: 'deny',
              reason: 'User sent an alternative message',
              skipResume: true,
            });
            if (denyResult.isErr()) {
              throw new Error('Failed to reject pending tool approval');
            }
          } else if (
            activeItem.toolKey === coco.COCO_SELECT_FOLDER_TOOL_KEY &&
            activeItem.part.isIncomplete?.() === true
          ) {
            // The user typed an alternative message instead of using the active
            // folder selector. Resolve the interrupted tool the same way the
            // selector's own Cancel button does (a `{ cancelled: true }`
            // interrupted-tool result), so it stops being incomplete and the
            // selector clears from the input. Scoped to coco/select-folder only:
            // other hijackers (credentials, load-resource) decline through their
            // own SubmitToolAction path with a structured payload and must not be
            // finished with this cancel shape. The tool call may live in a
            // sub-agent conversation, so target the queue item's conversationId.
            const cancelResult = await client.resource.chat.runInterruptedTool({
              conversationId: activeItem.conversationId ?? conversationId,
              toolCallId: activeItem.part.toolCallId,
              model: cocoModel.model,
              input: { cancelled: true },
            });
            if (cancelResult.isErr()) {
              throw new Error('Failed to cancel the folder selection');
            }
          }
        }
      }

      const result = await submitChatAction.execute(submitParams);
      if (result.isErr()) {
        const detail = result.error.find(
          (e): e is { t: string; message: string } =>
            'message' in e && e.message !== undefined
        )?.message;
        throw new Error(detail ?? 'Failed to send message');
      }
      // Message sent — drop the persisted draft so a reload can't resurrect it.
      removeChatDraft(conversationId);
      clearUrlSeedIfPresent();
    },
    [
      submitChatAction,
      runToolAction,
      client,
      hijackerQueueModel,
      conversationId,
      cocoModel.systemSuffix,
      cocoModel.model,
      clearUrlSeedIfPresent,
    ]
  );

  useEffect(() => {
    if (!hasUrlSeed) {
      return;
    }
    const persistedMessageCount = conversation?.getAllMessages().length ?? 0;
    if (persistedMessageCount > 0) {
      clearUrlSeedIfPresent();
    }
  }, [hasUrlSeed, conversation, clearUrlSeedIfPresent]);

  const initialArtifacts = usePendingSubmission(
    conversationId,
    submitChatAction,
    pending,
    cocoModel
  );

  const handleStop = useCallback(
    (message?: PromptInputMessage) => {
      void stopAction.execute({
        ...(message?.type === 'text'
          ? { text: message.text }
          : { editorState: message?.editorState }),
        files: message?.files,
        systemSuffix: cocoModel.systemSuffix,
      });
    },
    [stopAction, cocoModel.systemSuffix]
  );

  // Submitting mid-run enqueues instead of cancelling; MessageQueueModel drains
  // the queue in order (via handleSubmit) as each run settles.
  const handleQueue = useCallback(
    (message: PromptInputMessage) => {
      messageQueueModel?.enqueue(message);
    },
    [messageQueueModel]
  );

  // Give the queue model the submit path it drains through. handleSubmit's
  // identity changes with its deps, so keep the model's handler current.
  useEffect(() => {
    messageQueueModel?.setDrainHandler(handleSubmit);
  }, [messageQueueModel, handleSubmit]);

  // "Send now" opts a single queued message back into cancel-and-resend: drop it
  // from the queue and route it through handleStop (stop the run, send it).
  const handleQueuedSubmitNow = useCallback(
    (item: QueuedMessage) => {
      messageQueueModel?.remove(item.id);
      handleStop(item.message);
    },
    [messageQueueModel, handleStop]
  );

  const handleQueuedRemove = useCallback(
    (id: string) => {
      messageQueueModel?.remove(id);
    },
    [messageQueueModel]
  );

  const status = useWatchableValue<
    'ready' | 'streaming' | 'submitted' | 'error' | 'loading'
  >(cocoModel.statusWatchable);

  const transition = { duration: 0.2, ease: 'easeOut' } as const;
  const location = useLocation();
  const navigationType = useNavigationType();
  const isNewChat =
    (location.state as { newChat?: boolean } | null)?.newChat === true ||
    hasUrlSeed;

  // Chat surface + entrypoint tracking. Prefer the loaded conversation's surface
  // (authoritative for chats reopened from history); fall back to the seed for
  // freshly-created chats whose conversation hasn't loaded yet.
  const analytics = useAnalytics();
  const org = useWhoami((state) => state.org);
  const conversationSurface = useChatSurface();
  const seedSurface = useMemo(
    () => deriveSeedChatSurface(pending, isNewChat),
    [pending, isNewChat]
  );
  // Explicit entrypoint passed by the navigating component (sidebar,
  // command palette, share-link redirect) wins; otherwise derive it, treating a
  // POP navigation (direct load / refresh) as a deep link.
  const navEntrypoint = (
    location.state as { entrypoint?: ChatEntrypoint } | null
  )?.entrypoint;
  const isDirectLoad = navigationType === 'POP';
  const entrypoint = useMemo(
    () =>
      navEntrypoint ?? deriveChatEntrypoint(pending, isNewChat, isDirectLoad),
    [navEntrypoint, pending, isNewChat, isDirectLoad]
  );
  const resolvedSurface = conversationSurface ?? seedSurface;
  if (resolvedSurface !== null) {
    surfaceRef.current = resolvedSurface;
  }
  const chatViewedFiredRef = useRef<string | null>(null);
  useEffect(() => {
    if (
      resolvedSurface === null ||
      chatViewedFiredRef.current === conversationId
    ) {
      return;
    }
    chatViewedFiredRef.current = conversationId;
    analytics.track(CocoEvent.ChatViewed, {
      conversationId,
      orgId: org.id,
      surface: resolvedSurface,
      entrypoint,
    });
  }, [analytics, conversationId, org.id, resolvedSurface, entrypoint]);

  const hasLoadedOnceRef = useRef(false);
  if (messagesStatus === 'ready') {
    hasLoadedOnceRef.current = true;
  }
  const isLoading =
    messagesStatus === 'loading' && !isNewChat && !hasLoadedOnceRef.current;

  useRestoreChatDraft({
    cocoModel,
    conversationId,
    pending,
    isLoading,
    chatRef,
  });

  /**
   * Whether this chat exists because the reader asked to look at one resource:
   * a dataset opened from the Library, a workflow from Automations. Those land
   * on a freshly seeded chat with that resource already attached, and the panel
   * staying shut put the very thing they clicked behind the index card.
   *
   * Latched rather than read live. The URL seed is stripped as soon as the
   * conversation exists (see the effect calling `clearUrlSeedIfPresent`), and
   * the deferred variant is not visible on the first render, so reading either
   * directly would hand the split a `defaultSize` that changes under it.
   * Sticky-true for the life of the mount is what that layout needs.
   */
  const openedFromSeedRef = useRef(false);
  if (hasUrlSeed || cocoModel.hasDeferredSeedContext()) {
    openedFromSeedRef.current = true;
  }

  const seedContext = useMemo(
    () => resolveChatSeedContext(searchParams),
    [searchParams]
  );
  const seedChrome = useMemo(() => {
    if (seedContext === null) {
      return null;
    }
    const strategy = getChatSeedStrategy(seedContext.type);
    return strategy?.getChatChrome?.() ?? null;
  }, [seedContext]);
  const isChatSeedActive =
    (hasUrlSeed || cocoModel.hasDeferredSeedContext()) && messages.length === 0;
  const SeedHeading = seedChrome?.Heading;
  const SeedFooter = seedChrome?.Footer;
  const seedChromeProps = seedContext?.input;
  const seedHeading =
    isChatSeedActive &&
    SeedHeading !== undefined &&
    seedChromeProps !== undefined ? (
      <SeedHeading {...seedChromeProps} />
    ) : null;
  const seedFooter =
    isChatSeedActive &&
    SeedFooter !== undefined &&
    seedChromeProps !== undefined ? (
      <SeedFooter {...seedChromeProps} />
    ) : null;

  // The Organize dual-view opens with an organize-library artifact rather than a
  // URL seed, so it never reaches the seed chrome above. Give its empty chat the
  // same centered input + dynamic "ask Coco, or do it yourself" heading as every
  // other resource open. Check the persisted conversation artifact too (not just
  // the one-shot client seed), so it stays consistent on reopen and after reload.
  // "Empty" counts only VISIBLE messages: an organize chat is created eagerly with
  // a hidden Coco system message, which must not count as a sent message here.
  const hasVisibleMessages = messages.some((m) => !m.isHidden());
  const organizeArtifact = [
    ...initialArtifacts,
    ...(conversation?.ownChatArtifacts() ?? []),
  ].find((a) => a.type === ORGANIZE_ARTIFACT_TYPE);
  const organizeChatActive =
    !hasVisibleMessages && organizeArtifact !== undefined;
  // Lazy initializer: wrapped so useState stores the template, not its result.
  const [organizeHeading] = React.useState(() => pickOrganizeHeading());
  const organizeHeadingText = organizeHeading(
    organizeModeOf(organizeArtifact?.payload) === 'shared'
      ? 'the shared library'
      : 'your library'
  );

  const seededDisplayTitle = useWatchableValue(
    cocoModel.seededDisplayTitleWatchable
  );
  const optimisticTitle = useWatchableValue(cocoModel.optimisticTitleWatchable);
  const conversationTitle = useMemo(() => {
    // Optimistic rename wins until the server snapshot round-trips.
    if (optimisticTitle !== null && optimisticTitle.trim() !== '') {
      return optimisticTitle.trim();
    }
    const serverTitle = conversation?.title();
    if (serverTitle !== undefined && serverTitle.trim() !== '') {
      return serverTitle.trim();
    }
    return seededDisplayTitle;
  }, [conversation, seededDisplayTitle, optimisticTitle]);
  const { folderId: breadcrumbFolderId, folderScope: breadcrumbFolderScope } =
    useChatBreadcrumbFolder({
      conversation,
      cocoModel,
      searchParams,
      seedContext,
      isNewChat,
    });
  const chatHeaderMeta = useChatHeaderMeta(conversationId);
  // What the artifact panel says about the conversation it belongs to — its
  // access control, and nothing else. No title crumb: the page bar one row above
  // already names the chat, and repeating it costs ~110px of the narrowest bar
  // in the app to say nothing new. (It was worth that while the bars sat side by
  // side and nothing else said the artifact was *inside* the chat, which is why
  // `PanelHost` used to carry a title too — see `PanelUnifiedChrome.host`.)
  const isFocusView = focusViewArtifactId !== undefined;

  const panelHost = useMemo(
    () =>
      // No host in the focus view, and that is what restores the artifact's own
      // Share. `useHostOwnsSharing` reads exactly this: a host publishing an
      // access control claims its grant governs the panel, so resources inside
      // draw no Share of their own. On a page showing one artifact the chat's
      // grant is the wrong one to offer, so the claim is simply not made and
      // the resource's Share — which grants the resource — comes back.
      conversationId === '' || isFocusView
        ? undefined
        : {
            accessControl: <CocoChatPanelAccess meta={chatHeaderMeta} />,
          },
    [conversationId, chatHeaderMeta, isFocusView]
  );

  const closeArtifactFocusView = useCallback(() => {
    // Straight to the conversation, replacing rather than popping. Focus leaves
    // no history behind it (see `openArtifactFocusView`), so there is nothing to
    // pop that would not also leave the chat — and a cold-opened link has
    // nothing at all. One address in, one address out.
    navigate(
      chatHrefForArtifactFocusView({
        orgSlug,
        chatId: conversationId,
        searchParams,
      }),
      { replace: true }
    );
  }, [navigate, orgSlug, conversationId, searchParams]);

  const openArtifactFocusView = useCallback(
    (artifactId: string, artifactType: string) => {
      // See {@link artifactFocusViewHref}: on a seeded chat the artifact list
      // *is* the URL, so the address has to carry whichever artifact it names —
      // the chat's own seed only covers the one it was opened with.
      //
      // `replace`, because focus is a display state rather than a place. Pushed,
      // every artifact focused in a row would be another entry to walk back
      // through, so leaving a chat you had read six artifacts in meant Back six
      // times. Replacing keeps the stack the shape it had when expanding was a
      // layout change: Back means leave this chat, the toggle and Esc mean leave
      // focus.
      navigate(
        artifactFocusViewHref({
          orgSlug,
          chatId: conversationId,
          artifactId,
          artifactType,
          searchParams,
        }),
        { replace: true }
      );
    },
    [navigate, orgSlug, conversationId, searchParams]
  );

  const shareAction = useAction(ShareResourceLinkAction);
  const shareArtifact = useCallback(
    (artifact: ArtifactDescriptor) => {
      const resource = shareableResourceForArtifact(artifact);
      if (resource === null) {
        return;
      }
      // Explicit targets, so the dialog offers this resource alone. Left to
      // resolve its own target the action would find the chat model in scope —
      // this page still mounts the whole chat — and offer the conversation and
      // every artifact in it, which is the grant this page exists to narrow.
      void shareAction.execute({ targets: [resource] });
    },
    [shareAction]
  );

  const focusView = useMemo(
    () =>
      focusViewArtifactId === undefined
        ? undefined
        : {
            artifactId: focusViewArtifactId,
            onBack: closeArtifactFocusView,
            onSelectArtifact: openArtifactFocusView,
            onShare: shareArtifact,
          },
    [
      focusViewArtifactId,
      closeArtifactFocusView,
      openArtifactFocusView,
      shareArtifact,
    ]
  );
  const breadcrumbs = useResourceBreadcrumbs({
    resourceType: ResourceType.Chat,
    resourceId: conversationId,
    resourceName: conversationTitle,
    folderId: breadcrumbFolderId,
    folderScope: breadcrumbFolderScope,
  });

  // The title in the bar renames the chat in place. Passing `title` is what
  // tells the action it has nothing left to ask — the `⋯` entry calls the same
  // action with no payload and still gets its dialog.
  const renameChatAction = useAction(RenameChatAction);
  const renameChat = useCallback(
    (title: string) => {
      void renameChatAction.execute({ title });
    },
    [renameChatAction]
  );

  return (
    // Around the shell, not inside it: the header's toggle is the shell's
    // `actions` element and the card is far below in `ChatWithArtifactPanel`,
    // so this is the nearest ancestor the two have in common.
    <ArtifactsIndexVisibilityProvider>
      {/* Outside the shell, like the index visibility above it: the comments
          toggle is part of the shell's `actions` and the column is down beside
          the artifact panel, so this is the nearest ancestor they share.
          `artifact` because a chat has no document of its own — the rail points
          at whichever artifact is in front. */}
      <CommentsRailProvider accepts="artifact">
        <ResourcePageShell
          // Full view: an empty trail and no actions, which leaves the shell
          // drawing no bar at all on desktop. The artifact's own bar is then the
          // only one on screen, which is the point — it carries Back and the
          // artifact's Share where the chat's bar used to carry the chat's.
          breadcrumbs={isFocusView ? [] : breadcrumbs}
          onRenameCurrent={
            conversationId !== '' && !isFocusView ? renameChat : undefined
          }
          // Beside the chat's name rather than out in the actions cluster: the
          // star marks *this chat*, and at the far right of the bar it read as
          // one more page-level control among Share and the `⋯` menu. Sized down
          // from the default 28px box to sit on the crumb's line.
          //
          // Held back until the conversation has a message in it. Opening a
          // favorited resource mints an empty chat to host its artifact, and that
          // chat is not a thing anyone means to keep: its crumb read "Untitled"
          // beside a hollow star, next to the artifact's own filled one, which
          // said the favorited resource was not favorited. An empty chat offers
          // no star at all now; sending the first message is what makes it a chat
          // worth keeping, and the star appears with it.
          titleAdornment={
            conversationId !== '' && !isFocusView && messages.length > 0 ? (
              <FavoriteToggleButton
                type={ResourceType.Chat}
                id={conversationId}
                className="h-6 w-6"
                iconClassName="h-3.5 w-3.5"
              />
            ) : undefined
          }
          actions={
            conversationId !== '' && !isFocusView ? (
              <CocoChatActions meta={chatHeaderMeta} />
            ) : undefined
          }
          // Full view: no card here, because the artifact draws its own. Left on,
          // the two would nest and the page would carry a border inside a border.
          // The artifact's is the one to keep — it is the framing that resource
          // gets when opened from the Library, which is the whole point of matching.
          layout={isFocusView ? 'flat' : 'panel'}
        >
          <div className="flex-1 flex flex-col min-h-0 overflow-hidden h-full">
            {chatHeaderMeta.isArchived ? (
              <ArchivedNotice>
                This chat is archived. It no longer appears in the chats list or
                in the Library, but it stays readable and you can still post to
                it.
              </ArchivedNotice>
            ) : null}
            {/* The split sits under one bar that spans the window, so the Share in
            it visibly governs both panes — the grant covers the conversation
            and every artifact in it, and a bar per pane could not say that. The
            artifact gives up the bar’s height for it.

            Outside the loading swap below, which used to tear the split down
            and rebuild it on every load. */}
            {/* Not in the focus view. There the artifact fills the window and draws
              its own bar, and a column beside that split would start above the bar
              and cut it short of the window's edge. The resource inside the panel
              hosts the rail instead, below its bar, so the header spans the width.
              See `DocumentEditLayout`. */}
            <CommentsRailOutlet enabled={!isFocusView}>
              <ChatWithArtifactPanel
                conversation={conversation}
                conversationId={conversationId}
                // A chat seeded from a resource opens onto that resource.
                autoOpenOnArtifact={openedFromSeedRef.current}
                initialArtifacts={initialArtifacts}
                onRegisterArtifact={handleRegisterArtifact}
                onUnregisterArtifact={handleUnregisterArtifact}
                host={panelHost}
                onOpenFocusView={openArtifactFocusView}
                {...(focusView !== undefined && { focusView })}
              >
                <AnimatePresence mode="wait">
                  {isLoading ? (
                    <motion.div
                      key="skeleton"
                      className="h-full w-full flex flex-col min-h-0"
                      initial={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={transition}
                    >
                      <CocoChatSkeletonContent />
                    </motion.div>
                  ) : (
                    <motion.div
                      key="content"
                      className="h-full w-full flex flex-col min-h-0"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={transition}
                    >
                      <ChatTableActionsScope conversationId={conversationId}>
                        <PresentationalChat
                          status={status}
                          ref={chatRef}
                          messages={messages}
                          firstUnreadMessageId={firstUnreadMessageId}
                          onSubmit={handleSubmit}
                          useRichText
                          globalDrop
                          customToolRenderers={getV1ToolRenderer}
                          conversationId={conversationId}
                          currentUserId={currentUserId}
                          onStop={handleStop}
                          onQueue={handleQueue}
                          aboveComposer={
                            <QueuedMessages
                              items={queuedMessages}
                              onSubmitNow={handleQueuedSubmitNow}
                              onRemove={handleQueuedRemove}
                            />
                          }
                          messageActionsConfig={{
                            showReplay: false,
                            showEdit: false,
                          }}
                          onMentionClick={handleMentionClick}
                          onCitationClick={handleCitationClick}
                          inputFooter={
                            <CocoChatInputFooter chatRef={chatRef} />
                          }
                          customSubmitButton={
                            <CocoChatSubmitButton chatRef={chatRef} />
                          }
                          chatModel={cocoModel}
                          inputHeader={
                            seedHeading !== null ||
                            organizeChatActive ||
                            (headerContent !== null && !hasQueuedMessages) ? (
                              <div className="flex flex-col gap-2">
                                {seedHeading}
                                {organizeChatActive ? (
                                  <TypingTitle
                                    text={organizeHeadingText}
                                    className="mb-2"
                                  />
                                ) : null}
                                {headerContent !== null &&
                                !hasQueuedMessages ? (
                                  <CocoInputHeader
                                    header={headerContent.header}
                                    children={headerContent.children}
                                  />
                                ) : null}
                              </div>
                            ) : null
                          }
                          inputBelow={seedFooter}
                          centerInputWhenEmpty={
                            isChatSeedActive || organizeChatActive
                          }
                        />
                      </ChatTableActionsScope>
                    </motion.div>
                  )}
                </AnimatePresence>
              </ChatWithArtifactPanel>
            </CommentsRailOutlet>
          </div>
        </ResourcePageShell>
      </CommentsRailProvider>
    </ArtifactsIndexVisibilityProvider>
  );
};

const CocoChatResourceSelectionSync: React.FC = () => {
  const coco = useCocoChatModel();
  const selection = useInScopeModel(ResourceSelectionModel);
  const actionsContext = useActionsContext();
  const conversation = useWatchableValue(coco.conversationWatchable);
  const conversationId = coco.conversationId;

  const seededDisplayTitle = useWatchableValue(
    coco.seededDisplayTitleWatchable
  );
  const optimisticTitle = useWatchableValue(coco.optimisticTitleWatchable);
  const displayName = useMemo(() => {
    if (optimisticTitle !== null && optimisticTitle.trim() !== '') {
      return optimisticTitle.trim();
    }
    const t = conversation?.title();
    if (t !== undefined && t.trim() !== '') {
      return t.trim();
    }
    if (seededDisplayTitle !== null && seededDisplayTitle !== '') {
      return seededDisplayTitle;
    }
    return 'Untitled chat';
  }, [conversation, seededDisplayTitle, optimisticTitle]);

  useEffect(() => {
    if (selection === null || conversationId === '') {
      return;
    }
    selection.setSelections([
      {
        resourceType: ResourceType.Chat,
        resourceId: conversationId,
        name: displayName,
      },
    ]);
    actionsContext.targetModel(selection);
  }, [selection, conversationId, displayName, actionsContext]);

  return null;
};

const MessageReadSync: React.FC = () => {
  const cocoModel = useCocoChatModel();
  const conversationId = cocoModel.conversationId;
  const client = useTenantedClient();
  const messagesStatus = useWatchableValue<'loading' | 'ready'>(
    cocoModel.messagesStatusWatchable
  );
  const messages = useWatchableValue<MessageClass[]>(
    cocoModel.messagesWatchable
  );

  useMarkMessagesAsRead(
    conversationId,
    messagesStatus,
    messages.length,
    client.resource.chat
  );

  return null;
};

export const CocoChat: React.FC<{
  conversationId: string;
  systemSuffix?: string;
  /**
   * Render one of this chat's artifacts as the whole page instead of the
   * conversation. Set by the `/chats/:chatId/artifacts/:artifactId` route.
   *
   * The chat still mounts: it is what loads the conversation, derives the
   * artifact list, and supplies the access gate and model scopes every renderer
   * expects. What it does not do is draw itself — the panel's focus-view branch
   * never renders the chat pane, and the bar below drops the conversation's
   * breadcrumbs and actions.
   */
  focusViewArtifactId?: string;
}> = ({ conversationId, systemSuffix, focusViewArtifactId }) => {
  const client = useTenantedClient();

  const [searchParams] = useSearchParams();
  const urlSeed = useMemo(
    () => parseChatSeedFromSearchParams(searchParams),
    [searchParams]
  );
  const hasUrlSeed = urlSeed !== null;
  // URL seed survives refresh; sessionStorage covers other new-chat flows.
  const pending = useMemo(
    () => peekPendingChatSubmission(conversationId) ?? urlSeed,
    [conversationId, urlSeed]
  );
  const org = useWhoami((state) => state.org);
  const { enabled: isModelSwitchEnabled, isLoading: isFlagLoading } =
    useFeatureFlagWithLoading('coco-is-kimi');
  const overrideModel = isModelSwitchEnabled ? getCocoModelOverride() : null;

  // coco_chat_viewed now fires from CocoChatContent (once the surface is known).

  const userId = useWhoami((state) => state.whoami.id);
  const getBearerToken = useGetBearerToken();
  const queryClient = useQueryClient();
  const watchablesFactory = useWatchablesFactory();
  const modalManager = useModalManager((s) => s.actions);
  const location = useLocation();
  const state = location.state as {
    newChat?: boolean;
    autoApproveTools?: boolean;
    planMode?: boolean;
    folderId?: string;
  } | null;
  const folderIdForBackend =
    parseChatSeedFolderId(searchParams) ?? state?.folderId;
  /** When coming from new chat flow, use user's choice. Otherwise derive from async conversation.mode() when it loads. */
  const initialAutoApprove =
    state?.newChat === true || hasUrlSeed
      ? state?.autoApproveTools === true
      : undefined;
  /** Same as initialAutoApprove: honor the new-chat toggle; otherwise sync from the persisted conversation. */
  const initialPlanMode =
    state?.newChat === true || hasUrlSeed
      ? state?.planMode === true
      : undefined;

  const toolLoader = useMemo(
    () => createToolLoader(queryClient, org.id),
    [queryClient, org.id]
  );

  const workspaceDomainLoader = useMemo(
    () =>
      createWorkspaceDomainLoader({
        queryClient,
        orgId: org.id,
        resource: client.resource,
      }),
    [queryClient, org.id, client.resource]
  );

  const chatActions = useMemo(
    () => [
      new SubmitCocoChatAction(
        client.resource.chat,
        {
          agent: client.resource.agent,
          workflow: client.resource.workflow,
          dataset: client.resource.dataset,
        },
        org.id,
        userId,
        queryClient
      ),
      new CocoVoiceTranscriptionCompleteAction(),
      new ExecuteToolAction(client, getBearerToken, org.id),
      new SubmitToolAction(client.resource.chat),
      new SubmitToolResultsAction(),
      new ExecuteAllIncompleteToolCallsAction(),
      new ReplayAction(),
      new EditAction(),
      new CocoToggleAutoApproveAction(client.resource.chat),
      new CocoTogglePlanModeAction(client.resource.chat),
      new CocoCompactAction(client.resource.chat),
      new ViewLoadedToolsAction(modalManager),
      new SaveChatArtifactsAction(modalManager),
    ],
    [client, getBearerToken, org.id, queryClient, userId, modalManager]
  );

  // Create model inside ModelScopeFactory so each mount gets a fresh model.
  // This avoids the zombie state where a disposed model is re-added after
  // unmount/remount (e.g. React Strict Mode, route changes).
  const resolvedModel = pending?.model ?? overrideModel ?? undefined;

  const createModels = useCallback(() => {
    const chatModel = new CocoChatModel({
      conversationId,
      orgId: org.id,
      initialAutoApprove,
      initialPlanMode,
      initialModel: resolvedModel,
      systemSuffix,
      getToolLoader: () => toolLoader,
      getWorkspaceDomainLoader: () => workspaceDomainLoader,
      client: client.resource.chat,
      folderId: folderIdForBackend,
      watchablesFactory,
    });
    return [
      chatModel,
      new ChatArtifactModel(),
      new ResourceSelectionModel(),
      // HijackerQueueModel will get messages via setMessagesWatchable call from PresentationalChat
      new HijackerQueueModel(conversationId, chatModel.messagesWatchable),
      // Holds messages submitted while a run is in flight and drains them in
      // order (via the handler set in CocoChatContent) as each run settles.
      new MessageQueueModel(
        conversationId,
        chatModel.statusWatchable,
        chatModel.cancelledWatchable
      ),
    ];
  }, [
    conversationId,
    org.id,
    toolLoader,
    workspaceDomainLoader,
    resolvedModel,
    client.resource.chat,
    initialAutoApprove,
    initialPlanMode,
    systemSuffix,
    folderIdForBackend,
    watchablesFactory,
  ]);

  const deps = useMemo(
    () => [
      conversationId,
      org.id,
      toolLoader,
      workspaceDomainLoader,
      resolvedModel,
      watchablesFactory,
    ],
    [
      conversationId,
      org.id,
      toolLoader,
      workspaceDomainLoader,
      resolvedModel,
      watchablesFactory,
    ]
  );
  // Wait for feature flags to resolve before creating the chat model,
  // otherwise it will be created with the wrong default model.
  if (isFlagLoading) {
    return <CocoChatSkeletonPage />;
  }

  // Wrap the chat page in ModelScopeFactory so the CocoModel is available
  // throughout the page via useInScopeModel and actionsContext.
  // CocoChatContent uses useAvailableTools (suspends until tools load) - wrap in
  // Suspense so the app shell renders immediately; tools load via useEagerQuery.
  return (
    <Actions actions={chatActions}>
      <ModelScopeFactory
        createModels={createModels}
        deps={deps}
        key={conversationId}
      >
        <CocoChatResourceSelectionSync />
        <MessageReadSync />
        <Suspense fallback={<CocoChatSkeletonPage />}>
          <CocoChatContent
            pending={pending}
            hasUrlSeed={hasUrlSeed}
            {...(focusViewArtifactId !== undefined && { focusViewArtifactId })}
          />
        </Suspense>
      </ModelScopeFactory>
    </Actions>
  );
};

// Register the tool approval hijacker at module load time
// This hijacker takes precedence over other hijackers when a tool requires approval
registerApprovalHijacker(ToolApprovalHijacker);

// Register the tool approval footer
// Shows "Waiting for approval" at the end of the message chain when tools need approval
registerMessageFooter({
  id: 'tool-approval',
  priority: 10,
  showLatestOnly: true,
  shouldShow: hasToolNeedingApproval,
  render: ({ messages }) =>
    WaitingForApprovalPlaceholder({ count: getPendingApprovalCount(messages) }),
});

// Shows a pulsing "..." bubble while Coco is preparing a reply — either the
// last message is from the user (reply hasn't started) or an assistant
// message exists but hasn't emitted any visible text yet (streaming gap).
registerMessageFooter({
  id: COCO_THINKING_MESSAGE_FOOTER_ID,
  priority: 5,
  showLatestOnly: true,
  shouldShow: (messages, stackFrames) => {
    const last = messages[messages.length - 1];
    if (last === undefined) {
      return false;
    }
    //in case of error, cancellation, completion etc
    if (getStackFrameStatusInfo(stackFrames).isTerminal) {
      return false;
    }
    if (last.role === 'user') {
      return true;
    }
    if (last.role === 'assistant') {
      return true;
    }
    return false;
  },
  render: () => <CocoThinkingPlaceholder />,
});
